/**
 * The attendance screen's other views: the check-ins and outs as they arrive
 * from phones and readers, the designated check-in locations, and how face
 * and fingerprint readers send their feed.
 */
import { useState } from "react";
import {
  useHrAddGeofence,
  useHrClockEvents,
  useHrClockManual,
  useHrEditGeofence,
  useHrGeofences,
  useHrPeople,
} from "../../api/queries";
import { Button, Card, EmptyState, Field, Input, Select, Skeleton, Table } from "../../design/components";
import { METHOD, type ClockMethod } from "../../domain/hrClock";
import { t } from "../../i18n";
import { useHrAccess } from "./access";
import { Chip, h, PersonLink } from "./kit";
import { HrError, s } from "./shared";

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export function CheckIns({ day }: { day: string }) {
  const q = useHrClockEvents(day);
  const { can } = useHrAccess();
  const [method, setMethod] = useState("");
  const events = (q.data ?? []).filter((e) => !method || e.method === method);
  const by = (q.data ?? []).reduce<Record<string, number>>(
    (a, e) => ({ ...a, [e.method]: (a[e.method] ?? 0) + 1 }),
    {},
  );
  return (
    <>
      <div className={h.stats}>
        {(["phone_location", "terminal_face", "terminal_fingerprint", "manual"] as ClockMethod[]).map((m) => (
          <button
            key={m}
            type="button"
            className={h.stat}
            style={{ outline: method === m ? "2px solid var(--blue)" : undefined }}
            onClick={() => setMethod(method === m ? "" : m)}
            data-testid={`hr-ci-${m}`}
          >
            <span className={h.statN}>
              {METHOD[m].icon} {by[m] ?? 0}
            </span>
            <span className={h.statLabel}>{t(METHOD[m].label)}</span>
          </button>
        ))}
      </div>
      {q.isLoading && <Skeleton rows={6} />}
      {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
      {q.data && (
        <Card
          title={t("Check-ins and outs")}
          sub={t(
            "Newest first. Phone check-ins count only at approved locations.",
          )}
        >
          {events.length === 0 ? (
            <EmptyState>{t("No check-in recorded for this day.")}</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t("Time")}</th>
                  <th>{t("Person")}</th>
                  <th>{t("In or out")}</th>
                  <th>{t("How")}</th>
                  <th>{t("Where")}</th>
                  <th className={s.num}>{t("Distance")}</th>
                  <th>{t("Device")}</th>
                </tr>
              </thead>
              <tbody>
                {events.slice(0, 400).map((e) => (
                  <tr key={e.id} data-testid="hr-ci-row">
                    <td className={s.mono}>{time(e.at)}</td>
                    <td>
                      <PersonLink id={e.employeeId} name={e.name} />
                    </td>
                    <td>
                      <Chip colour={e.direction === "in" ? "var(--green)" : "var(--amber)"}>
                        {e.direction === "in" ? t("In") : t("Out")}
                      </Chip>
                    </td>
                    <td>
                      {METHOD[e.method]?.icon} {t(METHOD[e.method]?.label ?? e.method)}
                    </td>
                    <td>
                      {e.place ?? "—"}
                      {e.lat != null && e.lon != null && (
                        <>
                          {" "}
                          <a
                            href={`https://www.google.com/maps?q=${e.lat},${e.lon}`}
                            target="_blank"
                            rel="noreferrer"
                            title={t("Open the spot on a map")}
                          >
                            🗺
                          </a>
                        </>
                      )}
                    </td>
                    <td className={s.num}>{e.distanceM != null ? t("{m} m", { m: Math.round(e.distanceM) }) : "—"}</td>
                    <td className={s.dim}>{e.note ? `✍ ${e.note}` : (e.device ?? "—")}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}
      {can.write && <ManualCheckIn day={day} />}
    </>
  );
}

function ManualCheckIn({ day }: { day: string }) {
  const people = useHrPeople();
  const save = useHrClockManual();
  const [who, setWho] = useState("");
  const [dir, setDir] = useState<"in" | "out">("in");
  const [at, setAt] = useState("07:00");
  const [note, setNote] = useState("");
  const ok = who && note.trim().length >= 3;
  return (
    <Card
      title={t("Record by hand")}
      sub={t("Forgot a phone, or a reader failed? HR records it with a reason.")}
    >
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (ok)
            save.mutate(
              { employeeId: who, direction: dir, at: `${day}T${at}`, note },
              { onSuccess: () => setNote("") },
            );
        }}
      >
        <Field label="Person">
          <Select value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">{t("Choose a person")}</option>
            {(people.data?.employees ?? [])
              .filter((e) => e.status !== "left")
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name} · {e.employeeNo}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="In or out">
          <Select value={dir} onChange={(e) => setDir(e.target.value as "in" | "out")}>
            <option value="in">{t("In")}</option>
            <option value="out">{t("Out")}</option>
          </Select>
        </Field>
        <Field label="Time">
          <Input type="time" value={at} onChange={(e) => setAt(e.target.value)} />
        </Field>
        <Field label="Why">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t("e.g. Phone battery died; seen at the gate by the supervisor")}
          />
        </Field>
        <Button type="submit" variant="primary" disabled={!ok || save.isPending}>
          {t("Record")}
        </Button>
      </form>
      {save.error && <p className={s.err}>{save.error instanceof Error ? save.error.message : String(save.error)}</p>}
    </Card>
  );
}

export function Locations() {
  const q = useHrGeofences();
  const add = useHrAddGeofence();
  const edit = useHrEditGeofence();
  const { can } = useHrAccess();
  const [f, setF] = useState({ name: "", lat: "", lon: "", radius: "150", shift: "07:00", grace: "15" });
  const [geoErr, setGeoErr] = useState<string | null>(null);
  const valid =
    f.name.trim().length >= 2 &&
    Number.isFinite(Number(f.lat)) &&
    Number.isFinite(Number(f.lon)) &&
    f.lat !== "" &&
    f.lon !== "";
  const here = () =>
    navigator.geolocation?.getCurrentPosition(
      (p) => setF((x) => ({ ...x, lat: p.coords.latitude.toFixed(6), lon: p.coords.longitude.toFixed(6) })),
      () => setGeoErr(t("Couldn't get your location. Type the coordinates instead.")),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  return (
    <>
      <Card
        title={t("Where people may check in by phone")}
        sub={t(
          "Phone check-ins count only inside the circles HR allows each person.",
        )}
      >
        {q.isLoading && <Skeleton rows={3} />}
        {q.error && <HrError error={q.error} retry={() => void q.refetch()} />}
        {q.data &&
          (q.data.length === 0 ? (
            <EmptyState>
              {t("No locations yet. Add one below to allow phone check-ins.")}
            </EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>{t("Location")}</th>
                  <th>{t("Center")}</th>
                  <th className={s.num}>{t("Radius")}</th>
                  <th>{t("Shift starts")}</th>
                  <th className={s.num}>{t("Grace (min)")}</th>
                  <th>{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((g) => (
                  <tr key={g.id} data-testid="hr-fence-row">
                    <td>{g.name}</td>
                    <td className={s.mono}>
                      <a href={`https://www.google.com/maps?q=${g.lat},${g.lon}`} target="_blank" rel="noreferrer">
                        {g.lat.toFixed(5)}, {g.lon.toFixed(5)}
                      </a>
                    </td>
                    <td className={s.num}>{t("{m} m", { m: g.radiusM })}</td>
                    <td className={s.mono}>{g.shiftStart.slice(0, 5)}</td>
                    <td className={s.num}>{g.graceMin}</td>
                    <td>
                      {can.admin ? (
                        <Button
                          size="sm"
                          onClick={() => edit.mutate({ id: g.id, patch: { active: g.active === false } })}
                        >
                          {g.active === false ? t("Switch on") : t("Switch off")}
                        </Button>
                      ) : g.active === false ? (
                        t("Off")
                      ) : (
                        t("On")
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ))}
      </Card>
      {can.admin ? (
        <Card title={t("Add a location")} sub={t("Stand at the place and press the button, or type its coordinates.")}>
          <form
            className={s.form}
            onSubmit={(e) => {
              e.preventDefault();
              if (!valid) return;
              add.mutate(
                {
                  name: f.name.trim(),
                  lat: Number(f.lat),
                  lon: Number(f.lon),
                  radiusM: Number(f.radius) || 150,
                  shiftStart: f.shift,
                  graceMin: Number(f.grace) || 0,
                  active: true,
                },
                { onSuccess: () => setF({ name: "", lat: "", lon: "", radius: "150", shift: "07:00", grace: "15" }) },
              );
            }}
          >
            <Field label="Name">
              <Input
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
                placeholder={t("e.g. Fire station")}
                data-testid="hr-fence-name"
              />
            </Field>
            <Field label="Latitude">
              <Input
                value={f.lat}
                onChange={(e) => setF({ ...f, lat: e.target.value })}
                inputMode="decimal"
                data-testid="hr-fence-lat"
              />
            </Field>
            <Field label="Longitude">
              <Input
                value={f.lon}
                onChange={(e) => setF({ ...f, lon: e.target.value })}
                inputMode="decimal"
                data-testid="hr-fence-lon"
              />
            </Field>
            <Field label="Radius (m)">
              <Input value={f.radius} onChange={(e) => setF({ ...f, radius: e.target.value })} inputMode="numeric" />
            </Field>
            <Field label="Shift starts">
              <Input type="time" value={f.shift} onChange={(e) => setF({ ...f, shift: e.target.value })} />
            </Field>
            <Field label="Grace (min)">
              <Input value={f.grace} onChange={(e) => setF({ ...f, grace: e.target.value })} inputMode="numeric" />
            </Field>
            <Button onClick={here}>📍 {t("Use where I am now")}</Button>
            <Button type="submit" variant="primary" disabled={!valid || add.isPending} data-testid="hr-fence-add">
              {t("Add the location")}
            </Button>
          </form>
          {geoErr && <p className={s.err}>{geoErr}</p>}
          {add.error && <p className={s.err}>{add.error instanceof Error ? add.error.message : String(add.error)}</p>}
        </Card>
      ) : (
        <p className={s.note}>{t("Only the HR super admin adds or changes locations.")}</p>
      )}
    </>
  );
}

export function Readers() {
  const example = `POST /v1/hr/clock/terminal/batch
Authorization: Bearer <the reader system's integration key>

{ "events": [
  { "employee_no": "E1002", "direction": "in",  "at": "2026-09-27T06:52:10+03:00",
    "method": "fingerprint", "device": "GATE-2", "location": "Airfield maintenance base",
    "event_id": "8812" },
  { "employee_no": "E1047", "direction": "in",  "at": "2026-09-27T07:58:41+03:00",
    "method": "face", "device": "ADMIN-LOBBY", "event_id": "8813" }
] }`;
  return (
    <>
      <Card
        title={t("Face and fingerprint readers")}
        sub={t("Readers identify people and send check-ins here.")}
      >
        <ul className={h.list}>
          <li className={h.item}>
            <span style={{ fontSize: 20 }}>1</span>
            <span className={h.itemMain}>
              <b>{t("Enroll each person on the reader with their employee number")}</b>
              <span>
                {t(
                  "Faces and fingerprints stay on the reader. We get only who, when, and which reader.",
                )}
              </span>
            </span>
          </li>
          <li className={h.item}>
            <span style={{ fontSize: 20 }}>2</span>
            <span className={h.itemMain}>
              <b>{t("Give the reader system an integration key")}</b>
              <span>{t("Administration issues it. It can only post check-ins.")}</span>
            </span>
          </li>
          <li className={h.item}>
            <span style={{ fontSize: 20 }}>3</span>
            <span className={h.itemMain}>
              <b>{t("Point its feed at the platform")}</b>
              <span>
                {t(
                  "Send one event or a batch. Repeats count once; unknown employee numbers are reported.",
                )}
              </span>
            </span>
          </li>
        </ul>
        <pre className={s.note} style={{ whiteSpace: "pre-wrap", direction: "ltr", textAlign: "left", marginTop: 12 }}>
          {example}
        </pre>
        <p className={s.note}>
          {t(
            "Reader check-ins are refused for anyone HR hasn't set to use that reader. Each reader brand needs a small adapter.",
          )}
        </p>
      </Card>
    </>
  );
}
